import { useDataProvider, useNotify, useQueryWithStore } from "react-admin";
import React, { useState } from "react";
import { CheckOutlined, PlusOutlined } from "@ant-design/icons";
import { ExtendedDataProvider } from "../../../types";
import TeamCreateModal from "./TeamCreateModal";
import { getActiveMembership, setLocalStorageForUser } from "@src/components/Common/utils";

/**
 * The teams this user belongs to, the active one marked. Naming it above the
 * list as well would only be saying it twice, so the list is the whole control:
 * always open, with the active team in the brand colour.
 *
 * Team names are whatever someone typed, so every one of them is cut to the
 * panel and carries the whole thing in its tooltip.
 *
 * `onCreateTeam` hands the create dialog to the caller. A caller that renders
 * this inside a popover needs that: opening a modal from in here counts as a
 * click outside the popover, which closes it and takes the modal with it.
 */
const ActivateMembership = ({ onCreateTeam }: { onCreateTeam?: () => void }) => {
    const notify = useNotify();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const activeMembership = getActiveMembership();

    const { data: membershipsData } = useQueryWithStore({
        type: 'getFilterOptions',
        resource: 'user_memberships',
        payload: {
            pagination: { page: 1, perPage: 20 },
            sort: { field: 'id', order: 'asc' },
            filter: {}
        }
    });

    const handleActiveTeamChange = (id: any) => {
        if (id === 'null' || id == null) return;

        dataProvider.getAction('user_memberships', { id, verb: 'activate' })
            .then((response: any) => {
                notify('Active team changed');
                setLocalStorageForUser(response.data, null);
                window.location.reload();
            })
            .catch((error: any) => notify(JSON.stringify(error.body), 'error'));
    };

    const createTeam = () => {
        if (onCreateTeam) onCreateTeam();
        else setIsModalOpen(true);
    };

    return (
        <div className="topbar-team">
            <div className="topbar-team__list">
                {(membershipsData || []).map((membership: any) => {
                    const active = membership.id === activeMembership?.id;
                    return (
                        <button
                            key={membership.id}
                            type="button"
                            className={`topbar-team__item${active ? ' topbar-team__item--active' : ''}`}
                            onClick={() => handleActiveTeamChange(membership.id)}
                            title={membership.name}
                        >
                            <span className="topbar-team__item-name">{membership.name}</span>
                            {active && <CheckOutlined className="topbar-team__check" />}
                        </button>
                    );
                })}
            </div>

            <button type="button" className="topbar-team__create" onClick={createTeam}>
                <PlusOutlined />
                Create a new team
            </button>

            {!onCreateTeam && (
                <TeamCreateModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
            )}
        </div>
    );
};

export default ActivateMembership;
